import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { CreateFromQuoteInput } from '../../types';

interface QuoteInfo {
  id: string;
  quoteNumber: string;
  projectName?: string;
  total: string;
}

interface CreateFromQuoteModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (quoteId: string, data: CreateFromQuoteInput) => Promise<void>;
  quote: QuoteInfo | null;
  accountId: string;
  accountName?: string;
  isLoading?: boolean;
}

export function CreateFromQuoteModal({
  open,
  onClose,
  onSubmit,
  quote,
  accountId,
  accountName,
  isLoading,
}: CreateFromQuoteModalProps) {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quote) return;

    try {
      await onSubmit(quote.id, {
        account_id: accountId,
        account_name: accountName,
      });
      toast.success('Work order created from quote successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to create work order from quote: ${message}`);
      console.error('Error creating work order from quote:', error);
      // Keep form open for retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Work Order from Quote</DialogTitle>
          <DialogDescription>
            This will create a new work order with all items from the quote.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {quote && (
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Quote</span>
                <span className="font-mono font-medium">{quote.quoteNumber}</span>
              </div>
              {quote.projectName && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Project</span>
                  <span className="font-medium">{quote.projectName}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-medium">{quote.total}</span>
              </div>
              {accountName && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Account</span>
                  <span className="font-medium">{accountName}</span>
                </div>
              )}
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            All SKU items from the quote will be added as line items in the work order.
            You can then allocate inventory to fulfill the order.
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !quote}>
              {isLoading ? 'Creating...' : 'Create Work Order'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
